import { useState, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Search, Plus, Edit2, Trash2, Download, Printer, FileText } from 'lucide-react';
import { DataStore, Cost } from '@/lib/data-store';
import { CostForm } from '@/components/CostForm';
import { ProjectBreadcrumb } from '@/components/ProjectBreadcrumb';
import { useProject } from '@/context/ProjectContext';
import { generateCostCode, getNextCostNumber } from '@/lib/cost-utils';
import { exportToExcel, printTable } from '@/lib/export-utils';
import { printWithLetterhead, generateTableHTML } from '@/lib/print-utils';

const KosDetail = () => {
  const data = DataStore.getData();
  const [costs, setCosts] = useState<Cost[]>(data.costs);
  const [projects] = useState(data.projects);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterProjek, setFilterProjek] = useState('');
  const { selectedProject, setSelectedProject } = useProject();

  const handleAddNew = () => {
    setEditingId(null);
    setShowForm(true);
  };

  const handleEdit = (id: string) => {
    setEditingId(id);
    setShowForm(true);
  };

  const handleSubmit = (data: Cost) => {
    const newCosts = editingId
      ? costs.map(c => c.id === editingId ? { ...data, id: editingId } : c)
      : [...costs, data];

    setCosts(newCosts);
    DataStore.saveData({ ...DataStore.getData(), costs: newCosts });
    setShowForm(false);
  };

  const handleDelete = (id: string) => {
    const filtered = costs.filter(c => c.id !== id);
    setCosts(filtered);
    DataStore.saveData({ ...DataStore.getData(), costs: filtered });
  };

  const filteredCosts = useMemo(() => costs.filter(c => {
    const matchSearch = c.penerangan.toLowerCase().includes(searchTerm.toLowerCase()) ||
                       c.kodKos.toLowerCase().includes(searchTerm.toLowerCase());
    const matchFilter = !filterProjek || c.projek === filterProjek;
    const matchProject = !selectedProject || c.projek === selectedProject;
    return matchSearch && matchFilter && matchProject;
  }), [costs, searchTerm, filterProjek, selectedProject]);

  const totalKos = useMemo(() => filteredCosts.reduce((sum, c) => sum + c.jumlah, 0), [filteredCosts]);
  const nextCostCode = useMemo(() => generateCostCode(getNextCostNumber(costs)), [costs]);

  return (
    <div className="space-y-6">
      {selectedProject && (
        <ProjectBreadcrumb projectName={selectedProject} currentPage="cost" />
      )}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">6. Butiran Kos</h1>
          <p className="text-slate-600 mt-1">Pengurusan kos terperinci setiap item projek</p>
        </div>
        <div className="flex gap-3 flex-wrap">
          {costs.length > 0 && (
            <>
              <Button 
                onClick={() => {
                  const tableHTML = generateTableHTML(filteredCosts, [
                    { label: 'Kod Kos', key: 'kodKos' },
                    { label: 'Projek', key: 'projek' },
                    { label: 'Kategori', key: 'kategori' },
                    { label: 'Penerangan', key: 'penerangan' },
                    { label: 'Kuantiti', key: 'kuantiti' },
                    { label: 'Unit', key: 'unit' },
                    { label: 'Harga/Unit (RM)', key: 'hargaPerUnit', format: (v) => `RM ${v.toFixed(2)}` },
                    { label: 'Jumlah (RM)', key: 'jumlah', format: (v) => `RM ${v.toFixed(2)}` }
                  ]);
                  printWithLetterhead('BUTIRAN KOS PROJEK', tableHTML, 'SENARAI KOS / COST DETAILS');
                }} 
                className="bg-green-600 hover:bg-green-700 text-white gap-2 px-4 py-2 h-auto"
              >
                <Printer size={18} />
                <span className="font-semibold">Cetak</span>
              </Button>
              <Button 
                onClick={() => exportToExcel(filteredCosts, 'Butiran Kos')} 
                className="bg-blue-600 hover:bg-blue-700 text-white gap-2 px-4 py-2 h-auto"
              >
                <Download size={18} />
                <span className="font-semibold">Excel</span>
              </Button>
            </>
          )}
          <Button className="bg-red-600 hover:bg-red-700 gap-2 px-4 py-2 h-auto" onClick={handleAddNew}>
            <Plus size={18} />
            <span className="font-semibold">Kos Baru</span>
          </Button>
        </div>
      </div>

      {/* Summary Card */}
      <Card>
        <CardContent className="pt-6">
          <div className="space-y-2">
            <p className="text-sm text-slate-600">Jumlah Kos Keseluruhan</p>
            <p className="text-3xl font-bold text-orange-600">RM {totalKos.toFixed(2)}</p>
            <p className="text-xs text-slate-500">{filteredCosts.length} item kos</p>
          </div>
        </CardContent>
      </Card>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-3 text-slate-400" size={20} />
              <Input 
                placeholder="Cari item kos atau kod..." 
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 border-slate-300"
              />
            </div>

            {/* Filter by Project */}
            <select 
              value={filterProjek}
              onChange={(e) => setFilterProjek(e.target.value)}
              className="px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
            >
              <option value="">Semua Projek</option>
              {projects.map((p) => (
                <option key={p.id} value={p.namaProjek}>
                  {p.namaProjek}
                </option>
              ))}
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Costs Table */}
      <Card>
        <CardHeader>
          <CardTitle>Daftar Butiran Kos</CardTitle>
          <CardDescription>Jumlah {filteredCosts.length} item kos</CardDescription>
        </CardHeader>
        <CardContent>
          {filteredCosts.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              Tiada data kos. Sila tambahkan kos baru.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table id="costs-table" className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-3 px-4 font-semibold text-slate-700">Kod Kos</th>
                    <th className="text-left py-3 px-4 font-semibold text-slate-700">Projek</th>
                    <th className="text-left py-3 px-4 font-semibold text-slate-700">Kategori</th>
                    <th className="text-left py-3 px-4 font-semibold text-slate-700">Penerangan Item</th>
                    <th className="text-center py-3 px-4 font-semibold text-slate-700">Kuantiti</th>
                    <th className="text-center py-3 px-4 font-semibold text-slate-700">Unit</th>
                    <th className="text-right py-3 px-4 font-semibold text-slate-700">Harga/Unit (RM)</th>
                    <th className="text-right py-3 px-4 font-semibold text-slate-700">Jumlah (RM)</th>
                    <th className="text-left py-3 px-4 font-semibold text-slate-700">Tarikh</th>
                    <th className="text-left py-3 px-4 font-semibold text-slate-700">Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCosts.map((cost) => (
                    <tr key={cost.id} className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => setSelectedProject(cost.projek)}>
                      <td className="py-3 px-4 font-medium text-slate-900">{cost.kodKos}</td>
                      <td className="py-3 px-4 text-slate-600 text-sm font-medium text-blue-600 hover:underline">{cost.projek}</td>
                      <td className="py-3 px-4">
                        <span className="inline-block px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-700">
                          {cost.kategori}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-slate-600 text-sm">{cost.penerangan}</td>
                      <td className="py-3 px-4 text-center font-medium">{cost.kuantiti}</td>
                      <td className="py-3 px-4 text-center text-slate-600">{cost.unit}</td>
                      <td className="py-3 px-4 text-right text-slate-600">RM {cost.hargaPerUnit.toFixed(2)}</td>
                      <td className="py-3 px-4 text-right font-semibold text-orange-600">RM {cost.jumlah.toFixed(2)}</td>
                      <td className="py-3 px-4 text-slate-600 text-sm">{cost.tarikh}</td>
                      <td className="py-3 px-4 flex gap-2">
                        <button onClick={() => handleEdit(cost.id)} className="text-blue-600 hover:text-blue-700 text-sm gap-1 flex items-center">
                          <Edit2 size={14} /> Sunting
                        </button>
                        <button onClick={() => handleDelete(cost.id)} className="text-red-600 hover:text-red-700 text-sm gap-1 flex items-center">
                          <Trash2 size={14} /> Padam
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-slate-50 border-t-2 border-slate-200">
                    <td colSpan={7} className="py-3 px-4 text-right font-semibold text-slate-700">
                      Jumlah Total Kos:
                    </td>
                    <td className="py-3 px-4 text-right font-bold text-lg text-orange-600">
                      RM {totalKos.toFixed(2)}
                    </td>
                    <td colSpan={2}></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Cost Form Modal */}
      {showForm && (
        <CostForm
          cost={editingId ? costs.find(c => c.id === editingId) : undefined}
          kodKos={editingId ? (costs.find(c => c.id === editingId)?.kodKos || nextCostCode) : nextCostCode}
          projects={projects}
          onSubmit={handleSubmit}
          onCancel={() => setShowForm(false)}
        />
      )}
    </div>
  );
};

export default KosDetail;